import type { CSSProperties } from 'react';
import type { StyleOverrides } from '@docgen/schema';

/** A bare token name ("navy") becomes var(--navy); anything else is raw CSS. */
export function colorValue(v: string | undefined): string | undefined {
  if (v === undefined || v === '') return undefined;
  return /^[a-z][a-z0-9-]*$/i.test(v) ? `var(--${v})` : v;
}

const ALIGN: Record<string, CSSProperties['alignItems']> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
};
const JUSTIFY: Record<string, CSSProperties['justifyContent']> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  between: 'space-between',
};

export function styleToCss(style: StyleOverrides | undefined): CSSProperties {
  if (!style) return {};
  const css: CSSProperties = {};
  if (style.pad !== undefined) css.padding = style.pad;
  if (style.gap !== undefined) css.gap = style.gap;
  if (style.mt !== undefined) css.marginTop = style.mt;
  if (style.mb !== undefined) css.marginBottom = style.mb;
  if (style.bg !== undefined) css.background = colorValue(style.bg);
  if (style.color !== undefined) css.color = colorValue(style.color);
  if (style.width !== undefined) css.width = style.width;
  if (style.radius !== undefined) css.borderRadius = style.radius;
  if (style.align !== undefined) css.alignItems = ALIGN[style.align];
  if (style.justify !== undefined) css.justifyContent = JUSTIFY[style.justify];
  if (style.grow) css.flexGrow = 1;
  if (style.fontSize !== undefined) css.fontSize = style.fontSize;
  return css;
}

export const asText = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

/**
 * The DOM id / URL-fragment for a rendered page, from its resolved key
 * (`intro`, `program:0`, …). Used both to id each page's `<section>` and to
 * build a table-of-contents anchor link (`#pg-program-0`) that jumps to it —
 * in the preview and as a clickable link in the exported PDF.
 */
export function pageAnchorId(key: string): string {
  return `pg-${String(key).replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`;
}
